import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { AuditAction, AuditLogEntry } from '@osd/shared-types';
import { Alert } from '@osd/ui/alert';
import { Card } from '@osd/ui/card';
import { Field } from '@osd/ui/field';
import { Heading } from '@osd/ui/heading';
import { Input } from '@osd/ui/input';
import { Select } from '@osd/ui/select';
import { Spinner } from '@osd/ui/spinner';
import { Stack } from '@osd/ui/stack';
import { Table } from '@osd/ui/table';
import { Text } from '@osd/ui/text';
import { api, ApiError } from '../../lib/api';

/**
 * /admin/audit — admin-only.
 *
 * Read-only browser over `user_audit_log`. Newest-first; filterable
 * by target (UUID), actor (UUID), and action enum. Hard cap of 500
 * rows per fetch; "hasMore" flag is surfaced so the caller could
 * paginate (out of scope for v1 — we don't expect more than a few
 * thousand rows in the audit table).
 *
 * Self-service `register` rows show actor="system" (the tombstone
 * UUID `00000000-…`); we render a friendly marker for that.
 */
export const Route = createFileRoute('/admin/audit')({
  component: AuditPage,
  beforeLoad: () => {
    if (typeof window === 'undefined') return;
    const isAdmin = localStorage.getItem('osd.auth.isAdmin') === 'true';
    if (!isAdmin) {
      throw redirect({ to: '/' });
    }
  },
});

interface AuditActionOption {
  value: '' | AuditAction;
  label: string;
}

const ACTION_OPTIONS: AuditActionOption[] = [
  { value: '', label: 'Any action' },
  { value: 'approve', label: 'approve' },
  { value: 'reject', label: 'reject' },
  { value: 'promote', label: 'promote' },
  { value: 'demote', label: 'demote' },
  { value: 'disable', label: 'disable' },
  { value: 'enable', label: 'enable' },
  { value: 'invite_create', label: 'invite_create' },
  { value: 'invite_revoke', label: 'invite_revoke' },
  { value: 'register', label: 'register' },
];

const SYSTEM_ACTOR_ID = '00000000-0000-0000-0000-000000000000';

function AuditPage() {
  // Local filter form state — controlled, applied on demand (no auto-fetch
  // per keystroke; that would spam the API).
  const [target, setTarget] = useState('');
  const [actor, setActor] = useState('');
  const [action, setAction] = useState<'' | AuditAction>('');

  // Stable filter object so queryKey doesn't churn on every render.
  const filter = useMemo(
    () => ({
      target: target.trim() === '' ? undefined : target.trim(),
      actor: actor.trim() === '' ? undefined : actor.trim(),
      action: action === '' ? undefined : action,
      limit: 100,
    }),
    [target, actor, action],
  );

  const audit = useQuery({
    queryKey: ['admin', 'audit', filter],
    queryFn: () => api.admin().listAudit(filter),
    refetchInterval: 15_000,
  });

  return (
    <Stack gap={5}>
      <header>
        <Heading level={1}>Audit log</Heading>
        <Text muted>
          Every admin mutation + self-service register, newest-first. Filter by target user, actor,
          or action verb.
        </Text>
      </header>

      <Card>
        <Card.Title>Filters</Card.Title>
        <Card.Body>
          <Stack direction="row" gap={3} align="end">
            <Field label="Target user (UUID)">
              <Input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </Field>
            <Field label="Actor (UUID)">
              <Input
                value={actor}
                onChange={(e) => setActor(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </Field>
            <Field label="Action">
              <Select
                value={action}
                onChange={(e) => setAction(e.target.value as '' | AuditAction)}
              >
                {ACTION_OPTIONS.map((opt) => (
                  <option key={opt.value || 'any'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
          </Stack>
        </Card.Body>
      </Card>

      {audit.isLoading ? <Spinner size="sm" ariaLabel="Loading audit log" /> : null}
      {audit.isError ? (
        <Alert variant="danger">
          Failed to load audit log:{' '}
          {audit.error instanceof ApiError
            ? `HTTP ${audit.error.status}`
            : (audit.error as Error).message}
        </Alert>
      ) : null}

      {audit.data?.entries.length === 0 ? (
        <Card>
          <Text muted>No audit rows match these filters.</Text>
        </Card>
      ) : null}

      {audit.data && audit.data.entries.length > 0 ? (
        <Stack gap={2}>
          {audit.data.hasMore ? (
            <Text muted>
              Showing the first {audit.data.entries.length} rows. More exist — narrow the filters to
              see specific entries.
            </Text>
          ) : null}
          <Table columns="7rem 1fr 1fr" caption="Audit entries">
            <Table.Header>
              <Table.Row>
                <Table.Head>Action</Table.Head>
                <Table.Head>Actor → Target</Table.Head>
                <Table.Head>Timestamp</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {audit.data.entries.map((entry) => (
                <AuditTableRow key={entry.id} entry={entry} />
              ))}
            </Table.Body>
          </Table>
        </Stack>
      ) : null}
    </Stack>
  );
}

function AuditTableRow({ entry }: { readonly entry: AuditLogEntry }) {
  const isSystem = entry.actorUserId === SYSTEM_ACTOR_ID;
  return (
    <Table.Row>
      <Table.Cell>
        <code>{entry.action}</code>
      </Table.Cell>
      <Table.Cell>
        <strong>{isSystem ? 'system' : abbreviateUuid(entry.actorUserId)}</strong>
        {' → '}
        <strong>{abbreviateUuid(entry.targetUserId)}</strong>
        {entry.reason ? <em> · {entry.reason}</em> : null}
      </Table.Cell>
      <Table.Cell>{new Date(entry.ts).toLocaleString()}</Table.Cell>
    </Table.Row>
  );
}

function abbreviateUuid(uuid: string): string {
  // First + last 4 hex chars — enough to disambiguate within a session
  // without scrolling. Full UUID is one click away via the audit table.
  const head = uuid.slice(0, 4);
  const tail = uuid.slice(-4);
  return `${head}…${tail}`;
}
